import { Badge, Button, Flex, IconButton, Input, InputGroup, InputLeftElement, TableContainer, Tooltip, useToast } from "@chakra-ui/react";
import { IconEdit, IconPlus, IconFileSpreadsheet, IconFileX } from "@tabler/icons-react";
import { createColumnHelper } from "@tanstack/react-table";
import { DataTable } from "am-tax-fe-core";
import { ChangeEvent, FunctionComponent, useMemo, useState } from "react";
import { LuSearch } from "react-icons/lu";
import { TransactionModal } from "./TransactionModal";
import { TransactionNumberModal } from "./TransactionNumberModal";
import { useGetTransactionsByClientId } from "../../../api/transactions/transactionQueries";
import { downloadBenchmarkTemplateAsync, Transaction } from "../../../api/transactions/transactionApi";
import { SectionHeader } from "../../../components/SectionHeader";
import { useGetEntitiesByClientId } from "../../../api/entities/entityQueries";
import { getTransactionTypeDisplayName, getMethodDisplayName, TransactionType, Method } from "../../../utils/transactionDisplayNames";
import { createGetEntityNameById, getCategoryBadgeColor, getMethodBadgeColor } from "../../../utils/transactionHelpers";
import { downloadBenchmarkAnalysisAsync } from "../../../api/transactions/transactionApi";

// Transaction type for the table

interface Props {
    clientId?: string;
}

export const TransactionPanel: FunctionComponent<Props> = ({ clientId }) => {
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [transactionData, setTransactionData] = useState<Transaction | undefined>();
    const [showTransactionModal, setShowTransactionModal] = useState(false);
    const [transactionNumber, setTransactionNumber] = useState<number | undefined>(undefined);
    const { data: transactions = [], isLoading, refetch } = useGetTransactionsByClientId(clientId);

    const { data: entities = [], isLoading: isEntitiesLoading, refetch: refetchEntities } = useGetEntitiesByClientId(clientId);
    const toast = useToast();

    // Helper function to get entity name by ID
    const getEntityNameById = useMemo(() => createGetEntityNameById(entities), [entities]);

    const columnHelper = createColumnHelper<Transaction>();
    const [searchTerm, setSearchTerm] = useState<string>("");

    const filteredData = useMemo(() => {
        if (searchTerm.trim() === "") {
            return transactions;
        } else {
            const value = searchTerm.trim().toLowerCase();
            return transactions.filter(
                (c: Transaction) =>
                    c.name?.toLowerCase().includes(value) ||
                    c.category?.toLowerCase().includes(value) ||
                    c.type?.toLowerCase().includes(value) ||
                    c.method?.toLowerCase().includes(value) ||
                    (c.benchmarkAnalysisBlobPath && c.benchmarkAnalysisBlobPath.toLowerCase().includes(value)),
            );
        }
    }, [transactions, searchTerm, getEntityNameById]);

    async function refetchAll() {
        await refetch();
        await refetchEntities();
    }

    function enableEdit(row: Transaction) {
        setTransactionData(row);
        setShowTransactionModal(true);
    }

    function closeEdit(number: number = 1) {
        setShowEditDialog(false);
        if (number !== 0) {
            setTransactionNumber(number);
            setShowTransactionModal(true);
        }
        refetchAll();
    }

    function closeTransactionModal() {
        setTransactionData(undefined);
        setShowTransactionModal(false);
        setTransactionNumber(undefined);
        refetchAll();
    }

    function handleFilter(event: ChangeEvent<HTMLInputElement>) {
        const value = event.target.value;
        setSearchTerm(value);
    }

    async function downloadBenchmarkAnalysis(transaction: Transaction) {
        try {
            if (!transaction.benchmarkAnalysisBlobPath || transaction.benchmarkAnalysisBlobPath === "/") {
                toast({
                    title: "No File",
                    description: "No benchmark analysis file is available for this transaction",
                    status: "warning",
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            const response = await downloadBenchmarkAnalysisAsync(transaction.id);

            // Get the filename from the response headers or use a default
            const contentDisposition = response.headers.get("content-disposition");
            const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
            const filename = filenameMatch?.[1] || transaction.benchmarkAnalysisBlobPath.split("/").pop() || `benchmark_analysis_${transaction.name}.pdf`;

            // Create blob and download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            toast({
                title: "Download Complete",
                description: `Benchmark analysis for ${transaction.name} has been downloaded`,
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error("Error downloading benchmark analysis:", error);
            toast({
                title: "Download Failed",
                description: error instanceof Error ? error.message : "Failed to download benchmark analysis",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    }

    async function downloadBenchmarkTemplate() {
        try {
            toast({
                title: "Downloading Example Template",
                description: "Fetching benchmark analysis Excel template...",
                status: "info",
                duration: 2000,
                isClosable: true,
            });

            const response = await downloadBenchmarkTemplateAsync();

            const contentDisposition = response.headers.get("content-disposition");
            const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
            const filename = filenameMatch?.[1] || "Benchmark_Analysis_Template.xlsx";

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            toast({
                title: "Example Template Downloaded",
                description: "The benchmark analysis template has been downloaded.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error("Error downloading example template:", error);
            toast({
                title: "Download Failed",
                description: error instanceof Error ? error.message : "Failed to download example template",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    }

    return (
        <Flex flexFlow="column nowrap">
            <SectionHeader title="Transactions">
                <InputGroup flex="0 1 35rem">
                    <InputLeftElement pointerEvents="none">
                        <LuSearch />
                    </InputLeftElement>
                    <Input placeholder="Search Transactions" onChange={handleFilter} />
                </InputGroup>
                <Flex gap="0.5rem">
                    <Button leftIcon={<IconFileSpreadsheet />} variant="outline" flexShrink="0" onClick={downloadBenchmarkTemplate}>
                        Benchmark Analysis Example
                    </Button>
                    <Tooltip
                        label="Entities must be added before you can create a transaction"
                        isDisabled={!!entities && entities.length > 0}
                        shouldWrapChildren
                        placement="top"
                    >
                        <Button
                            leftIcon={<IconPlus />}
                            variant="primary"
                            flexShrink="0"
                            onClick={() => setShowEditDialog(true)}
                            isDisabled={!entities || entities.length === 0}
                        >
                            Add Transaction(s)
                        </Button>
                    </Tooltip>
                </Flex>
            </SectionHeader>
            <TableContainer overflowY="auto" sx={{ mt: "1rem", backgroundColor: "chakra-body-bg", maxHeight: "70vh" }}>
                <DataTable
                    enableFilters={false}
                    tableProps={{ size: "sm" }}
                    noData="No transactions found"
                    data={filteredData}
                    isLoading={isLoading || isEntitiesLoading}
                    defaultColumn={{ meta: { tableColumnHeaderProps: { pt: "1rem", zIndex: 1 } } }}
                    columns={[
                        columnHelper.accessor("name", {
                            header: "Name",
                            cell: props => props.getValue(),
                        }),
                        columnHelper.accessor("category", {
                            header: "Category",
                            cell: props => (
                                <Badge colorScheme={getCategoryBadgeColor(props.getValue())} variant="subtle" fontSize="xs" px={2} py={1} borderRadius="md">
                                    {props.getValue()}
                                </Badge>
                            ),
                        }),
                        columnHelper.accessor("type", {
                            header: "Transaction Type",
                            cell: props => getTransactionTypeDisplayName(props.getValue() as TransactionType),
                        }),
                        columnHelper.accessor("serviceProviders", {
                            header: "Service Providers",
                            cell: props => getEntityNameById(props.getValue()).join(", "),
                        }),
                        columnHelper.accessor("serviceRecipients", {
                            header: "Service Recipients",
                            cell: props => getEntityNameById(props.getValue()).join(", "),
                        }),
                        columnHelper.accessor("method", {
                            header: "Method",
                            cell: props => (
                                <Badge
                                    colorScheme={getMethodBadgeColor(props.getValue())}
                                    variant="subtle"
                                    fontSize="xs"
                                    px={3}
                                    py={1}
                                    borderRadius="full"
                                    fontWeight="bold"
                                >
                                    {getMethodDisplayName(props.getValue() as Method)}
                                </Badge>
                            ),
                        }),
                        columnHelper.accessor("benchmarkAnalysisBlobPath", {
                            header: "Benchmark Analysis",
                            cell: props => {
                                const transaction = props.row.original;
                                const hasFile = props.getValue() && props.getValue() !== "/";

                                return (
                                    <span>
                                        {hasFile ? (
                                            <>
                                                <IconFileSpreadsheet size={16} color="green" style={{ display: "inline", marginRight: "8px" }} />
                                                <button
                                                    onClick={() => downloadBenchmarkAnalysis(transaction)}
                                                    style={{
                                                        background: "none",
                                                        border: "none",
                                                        textDecoration: "underline",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    {props.getValue().split("/").pop()}
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <IconFileX size={16} color="gray" style={{ display: "inline", marginRight: "8px" }} />
                                                No file
                                            </>
                                        )}
                                    </span>
                                );
                            },
                        }),
                        {
                            id: "Edit Transaction",
                            cell: props => {
                                const row = props.row.original;
                                return (
                                    <Tooltip label="Edit Transaction">
                                        <IconButton
                                            icon={<IconEdit />}
                                            aria-label="Edit Transaction"
                                            variant="outline"
                                            onClick={evt => {
                                                evt.stopPropagation();
                                                enableEdit(row);
                                            }}
                                        />
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                />
            </TableContainer>

            <TransactionNumberModal show={showEditDialog} onClose={closeEdit} />
            <TransactionModal
                clientId={clientId}
                entities={entities}
                show={showTransactionModal}
                rowData={transactionData}
                onClose={closeTransactionModal}
                transactionNumber={transactionNumber}
            />
        </Flex>
    );
};
